//-- Copyright (c) 2021 TMT Insights Group, LLC
import { ThemeColors } from '@/__configs/config_types';
import { applyTemplateToOrderProfile, applyTemplateToOrderSpec } from '@/_utils/orderUtils';
import Datepicker from '@/components/_common/Datepicker/Datepicker';
import DeleteRowButton from '@/components/_common/DeleteRowButton';
import EmbargoDatepicker from '@/components/_common/EmbargoDatepicker';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { normalizeSchemaFormData } from '@/components/_common/SchemaForm/SchemaForm';
import useComponentOverride from '@/components/_hooks/useComponentOverride';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { useAppSelector } from '@/redux/hooks';
import { selectProfiles, selectWorkflows } from '@/redux/selectors/profileSelectors';
import { CustomerProfileData, FormJob, FormValues } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Box, CardHeader, Divider, Grid, Paper, darken, lighten } from '@mui/material';
import { isAfter } from 'date-fns';
import { FormikHelpers, useFormikContext } from 'formik';
import { useEffect, useRef, useState } from 'react';
import OPCustomerProfileFields from '../OPCustomerProfileFields';
import OPProfileDetailsOutput from '../OPProfileDetailsOutput';
import OrderProfileForm from './OrderProfileForm';

type ProfileDetailInfoProps = {
  job: FormJob;
  index: number;
  showDelete?: boolean;
  hasMultipleJobs?: boolean;
};

const ProfileDetailInfo = ({ job, index, showDelete = false, hasMultipleJobs }: ProfileDetailInfoProps) => {
  const { values, setFieldValue, touched, errors, setValues } = useFormikContext<FormValues>();
  const workflows = useAppSelector(selectWorkflows);
  const workflowName = job.profile?.data.workflow;
  const profiles = useAppSelector(selectProfiles);
  const disableDueDate = useConfigValue(['orderPlacementConfig', 'disableDueDate']);
  const disableEmbargoDate = useConfigValue(['orderPlacementConfig', 'disableEmbargoDate']);
  const outputRevisedLabelColor = useConfigValue(['orderPlacementConfig', 'outputRevisedLabelColor']);
  const orderProfileSchema =
    workflows &&
    Object.keys(workflows).length &&
    workflowName &&
    workflows[workflowName]?.order_profile_schema?.properties &&
    Object.keys(workflows[workflowName]?.order_profile_schema?.properties).length
      ? workflows[workflowName]?.order_profile_schema
      : undefined;
  const orderSpecSchema =
    workflows &&
    Object.keys(workflows).length &&
    workflowName &&
    workflows[workflowName]?.order_spec_schema?.properties &&
    Object.keys(workflows[workflowName]?.order_spec_schema?.properties).length
      ? workflows[workflowName]?.order_spec_schema
      : undefined;
  const isOrderProfileSchemaFormVisible = Boolean(orderProfileSchema?.properties && Object.keys(orderProfileSchema.properties).length);
  const orderSpecInitialized = useRef(false);

  const [prevProfile, setPrevProfile] = useState<CustomerProfileData['id'] | undefined>(job.profile?.id);

  const handleDueDateChange = (currentJob: FormJob, index: number, date: Date | null) => {
    if (currentJob.embargo_date && isAfter(new Date(currentJob.embargo_date), date as Date)) {
      setFieldValue(`jobs[${index}].due_date`, date?.toISOString());
      setFieldValue(`jobs[${index}].embargo_date`, undefined);
    } else {
      setFieldValue(`jobs[${index}].due_date`, date?.toISOString());
    }
  };

  const deleteProfile = () => {
    setValues({
      ...values,
      jobs: [...values.jobs.filter((_: FormJob, i: number) => i !== index)]
    });
  };

  const updateProfile = (updatedValues: CustomerProfileData) => {
    setValues({
      ...values,
      jobs: values.jobs.map((formJob: FormJob, i: number) => {
        if (i === index) {
          return {
            ...formJob,
            profile: { ...formJob.profile, data: updatedValues }
          };
        }
        return formJob;
      })
    });
  };

  const handleProfileSchemaFormChange = (data: { formData: Record<string, any> }) => {
    if (prevProfile === job.profile?.id) {
      setFieldValue(`jobs[${index}].order_profile_inputs`, data.formData);
    }
  };

  const handleOrderSpecInputsSave = (inputs: Record<string, any> | undefined) => {
    setFieldValue(`jobs[${index}].profile.data.order_spec_inputs`, inputs);
  };

  const isNewJob = index === values.jobs.length - 1;
  const isError = touched.jobs && touched.jobs[index]?.due_date && errors.jobs ? Boolean((errors as any).jobs[index]?.due_date) : false;

  useEffect(() => {
    const templateInputs = job.profile?.data?.template_inputs;
    if (!templateInputs) return;

    if (!job.order_profile_inputs && orderProfileSchema?.properties?.windows) {
      setFieldValue(`jobs[${index}].order_profile_inputs`, {
        windows: {
          start_date: undefined,
          end_date: undefined,
          tier: undefined,
          territories: []
        }
      });
    }

    if (orderSpecSchema) {
      const { updatedOrderSpecInputs, didChange } = applyTemplateToOrderSpec({
        templateInputs,
        currentOrderSpecInputs: job.profile?.data?.order_spec_inputs,
        orderSpecSchema
      });

      if (didChange) {
        setFieldValue(`jobs[${index}].profile.data.order_spec_inputs`, updatedOrderSpecInputs);
      }

      if (!orderSpecInitialized.current || prevProfile !== job.profile?.id) {
        orderSpecInitialized.current = true;
        const normalizedSchemaFormData = normalizeSchemaFormData(orderSpecSchema, updatedOrderSpecInputs || {});
        setFieldValue(`jobs[${index}].profile.data.order_spec_inputs`, normalizedSchemaFormData);
      }
    }

    if (orderProfileSchema) {
      const { updatedOrderProfileInputs, didChange } = applyTemplateToOrderProfile({
        templateInputs,
        currentOrderProfileInputs: job.order_profile_inputs,
        orderProfileSchema,
        shouldReset: prevProfile !== job.profile?.id
      });

      if (didChange) {
        setFieldValue(`jobs[${index}].order_profile_inputs`, updatedOrderProfileInputs);
      }
    }
    setTimeout(() => {
      setPrevProfile(job.profile?.id);
    }, 50);
  }, [
    job.profile?.data?.template_inputs,
    job.profile?.data?.order_spec_inputs,
    job.order_profile_inputs,
    orderSpecSchema,
    orderProfileSchema,
    index
  ]);

  return (
    <Paper>
      <Grid container key={`${job}_${index}`}>
        <Grid item xs={12} sm={8} sx={{ p: 1 }}>
          {isNewJob && <CardHeader title="Profile Details" />}
          <Box sx={{ p: 4 }}>
            <div>
              <Grid container spacing={5} sx={{ mb: 4 }}>
                <OPCustomerProfileFields jobIndex={index} profilesByCustomerName={profiles} hasMultipleJobs={hasMultipleJobs} />
              </Grid>
              <Grid container spacing={5}>
                <Grid item xs={12} md={6} lg={4}>
                  <Datepicker
                    label="Due Date"
                    required={!disableDueDate}
                    selected={disableDueDate ? undefined : new Date(job.due_date)}
                    showTimeSelect
                    onChange={(date) => handleDueDateChange(job, index, date)}
                    minDate={new Date()}
                    error={isError}
                    popperProps={{ strategy: 'fixed' }}
                    disabled={!!disableDueDate}
                    helperText={disableDueDate ? 'Externally managed' : undefined}
                  />
                </Grid>
                {!disableEmbargoDate && (
                  <Grid item xs={12} md={6} lg={4}>
                    <EmbargoDatepicker
                      selected={job.embargo_date ? new Date(job.embargo_date) : undefined}
                      dueDate={job.due_date}
                      onChange={(date) => setFieldValue(`jobs[${index}].embargo_date`, date)}
                    />
                  </Grid>
                )}
                <ProfileDetailInfoCustomFields handleChange={setFieldValue} job={job} jobIndex={index} />
              </Grid>

              {isOrderProfileSchemaFormVisible && prevProfile === job.profile?.id && (
                <Grid item xs={11}>
                  <Divider sx={{ mb: 3, mt: 3 }} />
                  <OrderProfileForm orderProfileSchema={orderProfileSchema} job={job} handleProfileSchemaFormChange={handleProfileSchemaFormChange} />
                </Grid>
              )}
            </div>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <OutputContainer className="output-container">
            {/* output box on the right side of an expanded section */}
            <Grid container sx={{ height: '100%' }}>
              {job.profile?.data?.workflow_type &&
              ['version_based', 'package', 'version_based_bundle'].indexOf(job.profile.data.workflow_type) !== -1 &&
              workflowName &&
              workflows[workflowName] &&
              (!workflows[workflowName].workflow_version ||
                workflows[workflowName].workflow_version !== 'v2' ||
                (workflows[workflowName].workflow_version === 'v2' && orderSpecSchema)) ? (
                <OPProfileDetailsOutput
                  profile={values.jobs[index].profile}
                  orderSpecSchema={orderSpecSchema}
                  orderSpecInputs={job.profile.data.order_spec_inputs}
                  updateOrderSpecInputs={handleOrderSpecInputsSave}
                  onSave={updateProfile}
                  outputRevisedLabelColor={outputRevisedLabelColor ? (outputRevisedLabelColor as ThemeColors) : undefined}
                />
              ) : (
                <Grid item container alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                  <PolarisIcon icon="files" width={120} style={{ opacity: 0.2 }} />
                </Grid>
              )}
            </Grid>
          </OutputContainer>
        </Grid>
      </Grid>
      {showDelete && values.jobs.length > 1 && <DeleteRowButton onClick={deleteProfile} sx={{ position: 'absolute', right: 16, top: 20 }} />}
    </Paper>
  );
};

export type ProfileDetailInfoCustomFieldsProps = {
  handleChange: FormikHelpers<FormValues>['setFieldValue'];
  job: FormJob;
  jobIndex: number;
};

const ProfileDetailInfoCustomFields = (props: ProfileDetailInfoCustomFieldsProps) =>
  useComponentOverride<ProfileDetailInfoCustomFieldsProps>(() => null, 'profileDetailInfoCustomFields', props);

export default ProfileDetailInfo;

const OutputContainer = styled.div`
  position: relative;
  height: 100%;
  border-radius: ${({ theme }) => `0px ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0px`};
  border-left: ${({ theme }) => (theme.palette.mode === 'light' ? `1px solid ${theme.palette.divider}` : 'none')};
  background: ${({ theme }) =>
    theme.palette.mode === 'light' ? darken(theme.palette.background.default, 0.02) : lighten(theme.palette.background.paper, 0.05)};
`;
